import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, UserPlus, Crown, ShieldCheck, ShieldOff } from "lucide-react";

export default function Members() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [workspace, setWorkspace] = useState(null);
  const [openInvite, setOpenInvite] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [busy, setBusy] = useState(false);

  const load = () =>
    Promise.all([api.get("/users"), api.get("/workspace")]).then(([u, w]) => { setMembers(u.data); setWorkspace(w.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const isSuperAdmin = user?.role === "superadmin";
  const hasAdmin = members.some((m) => m.role === "admin" || m.role === "superadmin");
  const meInList = members.find((m) => m.id === user?.id);
  const canSelfPromote = !hasAdmin && meInList?.role !== "admin";

  const onSelfPromote = async () => {
    if (!window.confirm(`Become workspace admin?`)) return;
    try { await api.post("/workspace/promote-self"); toast.success("You're now workspace admin"); window.location.reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const onChangeRole = async (m, newRole) => {
    if (m.id === user.id) return toast.error("Use a different admin to change your own role");
    if (!window.confirm(`Change ${m.name}'s role to ${newRole}?`)) return;
    try { await api.put(`/workspace/members/${m.id}/role`, { role: newRole }); toast.success(`${m.name} is now ${newRole}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const onRemove = async (m) => {
    if (m.id === user.id) return toast.error("Cannot remove yourself");
    if (!window.confirm(`Remove ${m.name}?`)) return;
    try { await api.delete(`/users/${m.id}`); toast.success(`${m.name} removed`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const onInvite = async (e) => {
    e.preventDefault();
    const domain = form.email.split("@")[1]?.toLowerCase().trim();
    // Only enforce domain restriction for non-superadmin
    if (!isSuperAdmin && (!domain || !workspace?.domains?.includes(domain))) return toast.error(`Email must be on a workspace domain: ${workspace?.domains?.join(", ")}`);
    setBusy(true);
    try { await api.post("/workspace/invite", form); toast.success(`${form.name} invited`); setOpenInvite(false); setForm({ name: "", email: "", password: "", role: "user" }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="p-10 max-w-7xl cf-fade">
      <div className="flex items-end justify-between mb-10 pb-6 border-b border-zinc-800 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// workspace / {workspace?.name || "\u2014"}</div>
          <h1 className="font-display text-5xl font-bold uppercase mt-2 tracking-tight">Members</h1>
          <div className="flex gap-2 flex-wrap mt-3">{workspace?.domains?.map((d) => <span key={d} className="cf-badge cf-badge-accent">{d}</span>)}</div>
        </div>
        <div className="flex gap-3 items-center">
          {canSelfPromote && <button onClick={onSelfPromote} data-testid="self-promote-button" className="cf-btn-primary"><Crown size={14} /> Become Workspace Admin</button>}
          {isAdmin && (
            <Dialog open={openInvite} onOpenChange={setOpenInvite}>
              <DialogTrigger asChild><button data-testid="invite-member-button" className="cf-btn-primary"><UserPlus size={14} /> Invite Teammate</button></DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-zinc-800 rounded-none text-white">
                <DialogHeader><DialogTitle className="font-display uppercase tracking-tight text-2xl">Invite Teammate</DialogTitle></DialogHeader>
                <p className="text-zinc-500 text-sm">
                  {isSuperAdmin
                    ? "As super admin, you can add users with any email domain."
                    : <>Email must be on a workspace domain: <span className="font-mono text-[#E23A28]">{workspace?.domains?.join(", ")}</span></>
                  }
                </p>
                <form onSubmit={onInvite} className="space-y-4 mt-4">
                  <div><label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Name</label><input data-testid="invite-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cf-input" /></div>
                  <div><label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Work email</label><input data-testid="invite-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="cf-input" placeholder={isSuperAdmin ? "anyone@anydomain.com" : `teammate@${workspace?.domains?.[0] || "company.com"}`} /></div>
                  <div><label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Temporary password</label><input data-testid="invite-password" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="cf-input" /></div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Role</label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger className="cf-input" data-testid="invite-role"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-zinc-800 rounded-none text-white"><SelectItem value="user">User</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <DialogFooter className="pt-4"><button type="submit" disabled={busy} data-testid="submit-invite" className="cf-btn-primary w-full justify-center">{busy ? "Inviting..." : "Send Invite"}</button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {!hasAdmin && <div className="cf-card p-5 mb-6 border-l-4 border-l-[#E23A28]" data-testid="no-admin-banner"><div className="font-mono text-[10px] uppercase tracking-widest text-[#E23A28] mb-1">// no admin yet</div><div className="text-zinc-300 text-sm">This workspace has no admin yet. Anyone here can claim it.</div></div>}

      <div className="cf-card">
        <table className="w-full">
          <thead><tr><th className="cf-table-header">Name</th><th className="cf-table-header">Email</th><th className="cf-table-header">Role</th><th className="cf-table-header">Joined</th><th className="cf-table-header text-right">Actions</th></tr></thead>
          <tbody>
            {members.map((m) => {
              const isSelf = m.id === user?.id;
              return (
                <tr key={m.id} className="cf-table-row" data-testid={`member-row-${m.id}`}>
                  <td className="cf-table-cell text-white font-medium">{m.name} {isSelf && <span className="text-zinc-500 font-mono text-xs ml-2">(you)</span>}</td>
                  <td className="cf-table-cell text-zinc-400 font-mono text-xs">{m.email}</td>
                  <td className="cf-table-cell"><span className={`cf-badge ${m.role === "superadmin" ? "cf-badge-accent" : m.role === "admin" ? "cf-badge-accent" : ""}`}>{m.role === "superadmin" ? "SUPER ADMIN" : m.role}</span></td>
                  <td className="cf-table-cell text-zinc-500 font-mono text-xs">{m.created_at?.slice(0, 10)}</td>
                  <td className="cf-table-cell text-right">
                    {isAdmin && !isSelf && (
                      <div className="flex justify-end gap-2">
                        {m.role === "superadmin" ? (
                          isSuperAdmin ? (
                            <button onClick={() => onChangeRole(m, "admin")} data-testid={`demote-${m.id}`} title="Demote to admin" className="text-zinc-500 hover:text-amber-400 transition-colors"><ShieldOff size={15} /></button>
                          ) : (
                            <span className="text-zinc-700 font-mono text-[10px] uppercase tracking-widest">// superadmin</span>
                          )
                        ) : m.role === "user" ? (
                          <button onClick={() => onChangeRole(m, "admin")} data-testid={`promote-${m.id}`} title="Promote to admin" className="text-zinc-500 hover:text-emerald-400 transition-colors"><ShieldCheck size={15} /></button>
                        ) : (
                          <button onClick={() => onChangeRole(m, "user")} data-testid={`demote-${m.id}`} title="Demote to user" className="text-zinc-500 hover:text-amber-400 transition-colors"><ShieldOff size={15} /></button>
                        )}
                        {m.role !== "superadmin" && (
                          <button onClick={() => onRemove(m)} data-testid={`remove-${m.id}`} title="Remove" className="text-zinc-500 hover:text-[#E23A28] transition-colors"><Trash2 size={15} /></button>
                        )}
                      </div>
                    )}
                    {!isAdmin && <span className="text-zinc-700 font-mono text-[10px] uppercase tracking-widest">// read only</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
